import { useDropzone } from 'react-dropzone';
import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import ApprovalPhotoCard from '../components/admin/ApprovalPhotoCard';
import type { Photo, Batch } from '../types';

function formatBytes(bytes: number) {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function uploadPhoto(
    batchId: string | undefined,
    file: File,
    onStage: (stage: string) => void,
    onProgress: (fraction: number) => void,
): Promise<boolean> {
    // Step 1: reserve a photo row and get a presigned PUT URL
    onStage('Preparing…');
    const prepRes = await apiFetch('/api/photos/upload/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ batchId, filename: file.name, size: file.size }),
    });
    if (!prepRes.ok) return false;
    const { photoId, uploadUrl } = await prepRes.json();

    // Step 2: PUT the binary directly to R2 — never touches the Worker
    onStage('Uploading…');
    const uploaded = await new Promise<boolean>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) onProgress(e.loaded / e.total);
        });
        xhr.addEventListener('load', () => resolve(xhr.status < 400));
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'image/jpeg');
        xhr.send(file);
    });
    if (!uploaded) return false;

    // Step 3: confirm — Worker verifies the object landed and kicks off variants
    onStage('Confirming…');
    const confirmRes = await apiFetch(`/api/photos/upload/confirm/${photoId}`, {
        method: 'POST',
        headers: authHeaders(),
    });
    return confirmRes.ok;
}

type UploadModalState = {
    open: boolean;
    done: boolean;
    cancelled: boolean;
    total: number;
    completed: number;
    failed: string[];
    currentFile: { name: string; size: number } | null;
    currentPreviewUrl: string | null;
    currentStage: string;
    currentProgress: number;
    recentResults: { name: string; ok: boolean }[];
};

const EMPTY_MODAL: UploadModalState = {
    open: false,
    done: false,
    cancelled: false,
    total: 0,
    completed: 0,
    failed: [],
    currentFile: null,
    currentPreviewUrl: null,
    currentStage: '',
    currentProgress: 0,
    recentResults: [],
};

const emptyMap = new Map() as Map<never, never>;

export default function Upload() {
    const navigate = useNavigate();
    const { batchId } = useParams<{ batchId: string }>();
    const [files, setFiles] = useState<File[]>([]);
    const [modal, setModal] = useState<UploadModalState>(EMPTY_MODAL);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [uploadedPhotos, setUploadedPhotos] = useState<Photo[]>([]);
    const [batch, setBatch] = useState<Pick<Batch, 'assignedAdId' | 'assignedAd' | 'adNotified' | 'batchType'> | null>(null);
    const [notifying, setNotifying] = useState(false);
    const [notifyError, setNotifyError] = useState('');
    const previewUrlRef = useRef<string | null>(null);
    const cancelledRef = useRef(false);
    const pausePromiseRef = useRef<Promise<void> | null>(null);
    const pauseResolveRef = useRef<(() => void) | null>(null);

    const fetchUploadedPhotos = () => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then((data: Photo[]) => setUploadedPhotos(Array.isArray(data) ? data : []))
            .catch(() => {});
    };

    useEffect(() => {
        fetchUploadedPhotos();
    }, [batchId]);

    useEffect(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => setBatch(data))
            .catch(() => {});
    }, [batchId]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { 'image/jpeg': ['.jpg', '.jpeg'] },
        onDrop: (accepted) => setFiles(prev => [...prev, ...accepted]),
    });

    const handleUpload = async () => {
        if (files.length === 0) return alert('Add at least one photo before uploading.');

        const snapshot = [...files];
        setFiles([]);
        cancelledRef.current = false;
        pausePromiseRef.current = null;
        pauseResolveRef.current = null;
        setShowCancelConfirm(false);
        setModal({ ...EMPTY_MODAL, open: true, total: snapshot.length });

        for (const file of snapshot) {
            if (pausePromiseRef.current) await pausePromiseRef.current;
            if (cancelledRef.current) break;

            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            const previewUrl = URL.createObjectURL(file);
            previewUrlRef.current = previewUrl;
            setModal(m => ({ ...m, currentFile: { name: file.name, size: file.size }, currentPreviewUrl: previewUrl, currentStage: 'Preparing…', currentProgress: 0 }));

            let ok = false;
            try {
                ok = await uploadPhoto(
                    batchId,
                    file,
                    stage => setModal(m => ({ ...m, currentStage: stage })),
                    progress => setModal(m => ({ ...m, currentProgress: progress })),
                );
            } catch {
                ok = false;
            }

            const resultName = file.name;
            setModal(m => ({
                ...m,
                currentStage: ok ? 'Done ✓' : 'Failed ✗',
                completed: m.completed + 1,
                failed: ok ? m.failed : [...m.failed, resultName],
                recentResults: [{ name: resultName, ok }, ...m.recentResults].slice(0, 5),
            }));

            await new Promise(r => setTimeout(r, 300));
        }

        setModal(m => ({ ...m, done: true, cancelled: cancelledRef.current, currentFile: null, currentStage: '' }));
        setShowCancelConfirm(false);
    };

    const handleCancelClick = () => {
        let resolve: () => void;
        pausePromiseRef.current = new Promise(r => { resolve = r; });
        pauseResolveRef.current = resolve!;
        setShowCancelConfirm(true);
    };

    const handleCancelConfirm = () => {
        cancelledRef.current = true;
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCancelDeny = () => {
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleNotifyAd = async () => {
        if (!batchId || !batch?.assignedAdId) return;
        setNotifying(true);
        setNotifyError('');
        try {
            const res = await apiFetch(`/api/batches/${batchId}/notify-ad`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (res.ok) {
                setBatch(prev => prev ? { ...prev, adNotified: true } : prev);
            } else {
                const data = await res.json().catch(() => ({}));
                setNotifyError(data.message || 'Failed to send notification');
            }
        } catch {
            setNotifyError('Failed to send notification');
        } finally {
            setNotifying(false);
        }
    };

    const closeModal = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setModal(EMPTY_MODAL);
        fetchUploadedPhotos();
    };

    const overallPercent = modal.total > 0 ? Math.round((modal.completed / modal.total) * 100) : 0;
    const bytePercent = Math.round(modal.currentProgress * 100);

    return (
        <div>
            <div className='upload-back-row'>
                <button className='back-link upload-back-link' onClick={() => navigate('/admin/photos')}>
                    ← Batches
                </button>
            </div>
            {batch?.batchType !== 'secondary' && uploadedPhotos.length > 0 && batch?.assignedAdId && !batch.adNotified && (
                <div className='upload-notify-bar'>
                    <span>{batch.assignedAd ? `${batch.assignedAd.fName} ${batch.assignedAd.lName} hasn't been notified yet.` : 'Artistic Staff not yet notified.'}</span>
                    <button className='app-nav-btn' onClick={handleNotifyAd} disabled={notifying}>
                        {notifying ? 'Sending…' : `Notify ${batch.assignedAd?.fName ?? 'Artistic Staff'}`}
                    </button>
                    {notifyError && <span className='upload-notify-error'>{notifyError}</span>}
                </div>
            )}
            {batch?.batchType !== 'secondary' && uploadedPhotos.length > 0 && batch?.adNotified && (
                <p className='upload-status-banner'>
                    {batch.assignedAd ? `${batch.assignedAd.fName} ${batch.assignedAd.lName} has been notified` : 'Artistic Staff notified'} — you can keep adding photos until their selections are submitted.
                </p>
            )}
            {batch?.batchType !== 'secondary' && uploadedPhotos.length > 0 && !batch?.assignedAdId && (
                <p className='upload-status-banner'>
                    No Artistic Staff member is assigned to this batch.{' '}
                    <button className='upload-inline-link' onClick={() => navigate('/admin/photos')}>
                        Visit the batch list to assign one via the edit button.
                    </button>
                </p>
            )}

            {batch?.batchType === 'secondary' ? (
                <p className='upload-status-banner'>
                    This is a secondary review batch. Photos are drawn from the original batches — no direct upload is needed.
                </p>
            ) : (
                <>
                    <div {...getRootProps()} className={isDragActive ? 'dropzone active' : 'dropzone'}>
                        <input {...getInputProps()} />
                        <p>Drag photos here, or click to select</p>
                        <button type='button' className='dropzone-select-btn'>Select Files</button>
                    </div>

                    {files.length > 0 && (
                        <p>{files.length} photo{files.length !== 1 ? 's' : ''} ready to upload</p>
                    )}

                    {files.length > 0 && (
                        <button className='dropzone-btn upload-btn' onClick={handleUpload}>
                            Upload {files.length} photo{files.length !== 1 ? 's' : ''}
                        </button>
                    )}
                </>
            )}

            {uploadedPhotos.length > 0 && (
                <div className='photo-grid'>
                    {uploadedPhotos.map(photo => (
                        <ApprovalPhotoCard
                            key={photo.id}
                            photo={photo}
                            photoStatusMap={emptyMap}
                            photoCreditsMap={emptyMap}
                            artistStatusMap={emptyMap}
                            batchIsCommitted={false}
                        />
                    ))}
                </div>
            )}

            {modal.open && (
                <div className='ai-modal-backdrop' role='dialog' aria-modal='true' aria-label='Upload progress'>
                    <div className='ai-modal'>
                        <div className='ai-modal-header'>
                            <h2 className='ai-modal-title'>
                                <span aria-hidden='true'>⬆</span> Uploading Photos
                            </h2>
                            {modal.done && (
                                <button className='ai-modal-close' onClick={closeModal} aria-label='Close'>×</button>
                            )}
                        </div>

                        {modal.currentFile && (
                            <div className='ai-modal-current'>
                                <div className='ai-modal-thumb'>
                                    <img src={modal.currentPreviewUrl ?? undefined} alt='' />
                                </div>
                                <div className='ai-modal-photo-info'>
                                    <span className='ai-modal-filename'>{modal.currentFile.name}</span>
                                    <span className='upload-modal-filesize'>{formatBytes(modal.currentFile.size)}</span>
                                                    <span className={`ai-modal-stage${modal.currentStage.startsWith('Done') ? ' done' : modal.currentStage.startsWith('Failed') ? ' upload-failed' : ''}`}>
                                        {modal.currentStage}
                                    </span>
                                    {modal.currentStage === 'Uploading…' && (
                                        <div className='upload-modal-byte-progress'>
                                            <div className='ai-modal-progress-bar-track'>
                                                <div className='ai-modal-progress-bar-fill' style={{ width: `${bytePercent}%` }} />
                                            </div>
                                            <span className='upload-modal-byte-pct'>{bytePercent}%</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div>
                            <div className='ai-modal-progress-row'>
                                <span className='ai-modal-count'>
                                    {modal.completed} of {modal.total} {modal.done ? (modal.cancelled ? 'uploaded — cancelled' : 'complete') : 'files'}
                                </span>
                            </div>
                            <div className='ai-modal-progress-bar-track upload-modal-overall-bar'>
                                <div className='ai-modal-progress-bar-fill' style={{ width: `${overallPercent}%` }} />
                            </div>
                        </div>

                        {modal.recentResults.length > 0 && (
                            <div className='upload-modal-results'>
                                {modal.recentResults.map((r, i) => (
                                    <span key={i} className={`upload-modal-result-chip${r.ok ? '' : ' failed'}`}>
                                        {r.ok ? '✓' : '✗'} {r.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {modal.done && (
                            <>
                                <div className='ai-modal-summary'>
                                    <div className='ai-modal-summary-item'>
                                        <span className='ai-summary-value'>{modal.completed - modal.failed.length}</span>
                                        <span className='ai-summary-label'>Uploaded</span>
                                    </div>
                                    {modal.failed.length > 0 && (
                                        <div className='ai-modal-summary-item'>
                                            <span className='ai-summary-value upload-modal-fail-count'>{modal.failed.length}</span>
                                            <span className='ai-summary-label'>Failed</span>
                                        </div>
                                    )}
                                </div>
                                {modal.failed.length > 0 && (
                                    <div>
                                        <p className='upload-fail-heading'>{modal.failed.length} upload{modal.failed.length !== 1 ? 's' : ''} failed:</p>
                                        <ul className='upload-fail-list'>
                                            {modal.failed.map(name => <li key={name}>{name}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}

                        {showCancelConfirm && (
                            <div className='ai-cancel-confirm'>
                                <p>Stop uploading? Photos uploaded so far will be kept.</p>
                                <div className='ai-cancel-confirm-btns'>
                                    <button className='ai-cancel-deny-btn' onClick={handleCancelDeny}>Keep going</button>
                                    <button className='ai-cancel-confirm-btn' onClick={handleCancelConfirm}>Stop</button>
                                </div>
                            </div>
                        )}

                        <div className='ai-modal-footer'>
                            {!modal.done && !showCancelConfirm && (
                                <button className='ai-modal-cancel-btn' onClick={handleCancelClick}>Cancel</button>
                            )}
                            {modal.done && (
                                <button className='ai-modal-done-btn' onClick={closeModal}>Done</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
